import React, { useState } from 'react';
import { formatCurrency } from '../../../utils/formatters';
import { PaymentData } from '../types';
import { Investment } from '../../../types';
import { TrendingUp, DollarSign, Calendar, BarChart3, Target, Award } from 'lucide-react';

interface BuybackPricesTabProps {
  paymentData: PaymentData;
  investment: Investment;
}

const BuybackPricesTab: React.FC<BuybackPricesTabProps> = ({ paymentData, investment }) => {
  const [selectedYear, setSelectedYear] = useState<number>(1);
  const [viewMode, setViewMode] = useState<'annual' | 'monthly'>('annual');
  
  // Group buyback prices by year
  const pricesByYear = paymentData.buybackPrices.reduce((acc, price) => {
    if (!acc[price.year]) {
      acc[price.year] = [];
    }
    acc[price.year].push(price);
    return acc;
  }, {} as Record<number, typeof paymentData.buybackPrices>);

  // Calculate appreciation metrics
  const initialPrice = investment.certificateBasePrice;
  const finalPrice = paymentData.buybackPrices[paymentData.buybackPrices.length - 1]?.price || initialPrice;
  const totalAppreciation = ((finalPrice - initialPrice) / initialPrice) * 100;
  const cagr = Math.pow(finalPrice / initialPrice, 1 / 30) - 1;
  
  // Calculate 5-year appreciation
  const fiveYearPrice = paymentData.buybackPrices.find(p => p.year === 5 && p.month === 12)?.price || initialPrice;
  const fiveYearAppreciation = ((fiveYearPrice - initialPrice) / initialPrice) * 100;
  const fiveYearCAGR = Math.pow(fiveYearPrice / initialPrice, 1 / 5) - 1;

  return (
    <div className="space-y-8">
      {/* Appreciation Summary */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-green-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-800">Resumen de Apreciación</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl">
            <div className="text-sm text-blue-600 mb-1">Precio Inicial</div>
            <div className="text-xl font-bold text-blue-700">
              {formatCurrency(initialPrice, investment.currencyFormat)}
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl">
            <div className="text-sm text-purple-600 mb-1">Precio Año 5</div>
            <div className="text-xl font-bold text-purple-700">
              {formatCurrency(fiveYearPrice, investment.currencyFormat)}
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl">
            <div className="text-sm text-green-600 mb-1">Precio Final (Año 30)</div>
            <div className="text-xl font-bold text-green-700">
              {formatCurrency(finalPrice, investment.currencyFormat)}
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-amber-50 to-amber-100 p-4 rounded-xl">
            <div className="text-sm text-amber-600 mb-1">Apreciación 5 años</div>
            <div className="text-xl font-bold text-amber-700">
              {fiveYearAppreciation.toFixed(1)}%
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-teal-50 to-teal-100 p-4 rounded-xl">
            <div className="text-sm text-teal-600 mb-1">Apreciación Total</div>
            <div className="text-xl font-bold text-teal-700">
              {totalAppreciation.toFixed(1)}%
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-xl">
            <div className="text-sm text-orange-600 mb-1">CAGR</div>
            <div className="text-xl font-bold text-orange-700">
              {(cagr * 100).toFixed(1)}%
            </div>
          </div>
        </div>
      </div>

      {/* View Mode Selector */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
            <Calendar className="w-5 h-5 text-blue-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-800">Precios de Recompra Detallados</h3>
        </div>
        
        <div className="mb-6 flex gap-4">
          <button
            onClick={() => setViewMode('annual')}
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              viewMode === 'annual'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Vista Anual (30 años)
          </button>
          <button
            onClick={() => setViewMode('monthly')}
            className={`px-6 py-3 rounded-lg font-medium transition-colors ${
              viewMode === 'monthly'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Vista Mensual Detallada
          </button>
        </div>
        
        {viewMode === 'monthly' && (
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Seleccionar Año para Vista Mensual
            </label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {Array.from({ length: 30 }, (_, i) => i + 1).map(year => (
                <option key={year} value={year}>
                  Año {year}
                </option>
              ))}
            </select>
          </div>
        )}
        
        {/* Annual Summary Table */}
        {viewMode === 'annual' && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Año
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Precio Inicial del Año
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Precio Final del Año
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Apreciación Anual
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    % Apreciación Anual
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Apreciación Acumulada
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Array.from({ length: 30 }, (_, i) => i + 1).map(year => {
                  const yearPrices = pricesByYear[year] || [];
                  const startPrice = yearPrices[0]?.price || initialPrice;
                  const endPrice = yearPrices[yearPrices.length - 1]?.price || initialPrice;
                  const annualAppreciation = endPrice - startPrice;
                  const annualAppreciationPercentage = startPrice > 0 ? ((endPrice - startPrice) / startPrice) * 100 : 0;
                  const cumulativeAppreciation = endPrice - initialPrice;
                  
                  return (
                    <tr key={year} className={`hover:bg-gray-50 ${year === 5 ? 'bg-purple-50' : year === 30 ? 'bg-green-50' : ''}`}>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {year}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-600">
                        {formatCurrency(startPrice, investment.currencyFormat)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-green-600">
                        {formatCurrency(endPrice, investment.currencyFormat)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-blue-600">
                        {formatCurrency(annualAppreciation, investment.currencyFormat)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-purple-600">
                        {annualAppreciationPercentage.toFixed(2)}%
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-teal-600">
                        {formatCurrency(cumulativeAppreciation, investment.currencyFormat)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Monthly Prices for Selected Year */}
        {viewMode === 'monthly' && (
          <div className="overflow-x-auto">
            <h4 className="text-lg font-medium text-gray-800 mb-4">Detalle Mensual - Año {selectedYear}</h4>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mes
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fecha
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Precio de Recompra
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Apreciación Mensual
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Apreciación Acumulada
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    % Apreciación Total
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(pricesByYear[selectedYear] || []).map((price, index) => {
                  const previousPrice = index > 0 ? pricesByYear[selectedYear][index - 1].price : 
                    (selectedYear > 1 ? pricesByYear[selectedYear - 1]?.[11]?.price || initialPrice : initialPrice);
                  const monthlyAppreciation = price.price - previousPrice;
                  const appreciationAmount = price.price - initialPrice;
                  const appreciationPercentage = ((price.price - initialPrice) / initialPrice) * 100;
                  
                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {price.month}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {price.date}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-green-600">
                        {formatCurrency(price.price, investment.currencyFormat)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-purple-600">
                        {formatCurrency(monthlyAppreciation, investment.currencyFormat)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-blue-600">
                        {formatCurrency(appreciationAmount, investment.currencyFormat)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-teal-600">
                        {appreciationPercentage.toFixed(2)}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Key Milestones */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
            <Target className="w-5 h-5 text-amber-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-800">Hitos Importantes</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <Award className="w-5 h-5 text-blue-600" />
              <h4 className="font-medium text-blue-800">Fin de Pagos</h4>
            </div>
            <div className="text-lg font-bold text-blue-700">Mes 48</div>
            <div className="text-sm text-blue-600">Certificados 100% pagados</div>
          </div>
          
          <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <h4 className="font-medium text-green-800">Inicio Producción</h4>
            </div>
            <div className="text-lg font-bold text-green-700">Año 5</div>
            <div className="text-sm text-green-600">Primeros ingresos</div>
          </div>
          
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-5 h-5 text-purple-600" />
              <h4 className="font-medium text-purple-800">Máxima Apreciación</h4>
            </div>
            <div className="text-lg font-bold text-purple-700">Año 5</div>
            <div className="text-sm text-purple-600">{formatCurrency(fiveYearPrice, investment.currencyFormat)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuybackPricesTab;